import axios from 'axios';
import { HOST_API } from '../../../config-global';

/**
 * API адаптер для работы с реальным бэкендом курсов (версия V1)
 * 
 * Структура:
 * - Курсы (courses)
 * - Потоки (flows) - запуски курса с датами
 * - Тарифы (tariffs) - условия доступа и привязка к продуктам
 */

// Axios instance для курсов V1
const courseAPI = axios.create({
    baseURL: HOST_API,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Interceptor для добавления токена авторизации и логирования
courseAPI.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        console.log('[API] Request:', config.method.toUpperCase(), config.baseURL + config.url);
        return config;
    },
    (error) => Promise.reject(error)
);

// Interceptor для логирования ответов
courseAPI.interceptors.response.use(
    (response) => {
        console.log('[API] Response:', response.status, response.config.url, response.data);
        return response;
    },
    (error) => {
        console.error('[API] Error:', error.config?.url, error.response?.status, error.response?.data);
        return Promise.reject(error);
    }
);

// Вспомогательная функция для проверки успеха ответа
const ensureSuccess = (data, context = 'API') => {
    if (data && data.success === false) {
        console.error(`[${context}] Ошибка от сервера:`, data);
        throw new Error(data.data || data.message || `Ошибка в ${context}`);
    }
    return data;
};

// ============================================
// КУРСЫ (Courses)
// ============================================

/**
 * Получить список курсов для магазина
 * @param {string} shopId - ID магазина
 * @returns {Promise<Array>} - Список курсов
 */
export const fetchCourses = async (shopId) => {
    const response = await courseAPI.get(`/courses/${shopId}`);
    // Обрабатываем разные форматы ответа от сервера
    const data = response.data;

    // Если это массив - возвращаем как есть
    if (Array.isArray(data)) {
        return data;
    }

    // Если объект с полем results/items/courses - возвращаем это поле
    if (data && typeof data === 'object') {
        if (Array.isArray(data.results)) return data.results;
        if (Array.isArray(data.items)) return data.items;
        if (Array.isArray(data.courses)) return data.courses;
        if (Array.isArray(data.data)) return data.data;
    }

    // Иначе возвращаем пустой массив
    console.warn('Unexpected response format from fetchCourses:', data);
    return [];
};

/**
 * Получить один курс по ID
 * @param {string} shopId - ID магазина
 * @param {string} courseId - ID курса
 * @returns {Promise<Object>} - Данные курса
 */
export const fetchCourse = async (shopId, courseId) => {
    const response = await courseAPI.get(`/courses/${shopId}/${courseId}`);
    const result = ensureSuccess(response.data, 'fetchCourse');

    // Если ответ содержит поле data, возвращаем его
    if (result && result.data) {
        return result.data;
    }

    // Иначе возвращаем весь ответ
    return result;
};

/**
 * Создать новый курс
 * @param {string} shopId - ID магазина
 * @param {Object} courseData - Данные курса
 * @param {string} courseData.title - Название курса
 * @param {string} [courseData.description] - Описание курса (опционально)
 * @param {string} [courseData.image] - URL изображения
 * @returns {Promise<Object>} - Созданный курс
 */
export const createCourse = async (shopId, courseData) => {
    const response = await courseAPI.post(`/courses/${shopId}`, courseData);
    const result = ensureSuccess(response.data, 'createCourse');
    return result && result.data ? result.data : result;
};

/**
 * Обновить курс
 * @param {string} shopId - ID магазина
 * @param {string} courseId - ID курса
 * @param {Object} courseData - Обновленные данные
 * @returns {Promise<Object>} - Обновленный курс
 */
export const updateCourse = async (shopId, courseId, courseData) => {
    const response = await courseAPI.patch(`/courses/${shopId}/${courseId}`, courseData);
    const result = ensureSuccess(response.data, 'updateCourse');
    return result && result.data ? result.data : result;
};

/**
 * Удалить курс
 * @param {string} shopId - ID магазина
 * @param {string} courseId - ID курса
 * @returns {Promise}
 */
export const deleteCourse = async (shopId, courseId) => {
    const response = await courseAPI.delete(`/courses/${shopId}/${courseId}`);
    ensureSuccess(response.data, 'deleteCourse');
};

// ============================================
// ПОТОКИ (Flows)
// ============================================

/**
 * Получить список потоков курса
 * @param {string} shopId - ID магазина
 * @param {string} courseId - ID курса
 * @returns {Promise<Array>} - Список потоков
 */
export const fetchFlows = async (shopId, courseId) => {
    const response = await courseAPI.get(`/courses/${shopId}/${courseId}/flows`);
    const data = response.data;

    // Проверка на ошибку от сервера
    if (data && data.success === false) {
        console.error('[API] fetchFlows - ошибка от сервера:', data);
        throw new Error(data.data || data.message || 'Ошибка загрузки потоков');
    }

    // Обрабатываем разные форматы ответа
    if (Array.isArray(data)) {
        return data;
    }

    if (data && typeof data === 'object') {
        if (Array.isArray(data.results)) return data.results;
        if (Array.isArray(data.items)) return data.items;
        if (Array.isArray(data.flows)) return data.flows;
        if (Array.isArray(data.data)) return data.data;
    }

    console.warn('Unexpected response format from fetchFlows:', data);
    return [];
};

/**
 * Получить один поток
 * @param {string} shopId - ID магазина
 * @param {string} courseId - ID курса
 * @param {string} flowId - ID потока
 * @returns {Promise<Object>} - Данные потока
 */
export const fetchFlow = async (shopId, courseId, flowId) => {
    const response = await courseAPI.get(`/courses/${shopId}/${courseId}/flows/${flowId}`);
    const result = ensureSuccess(response.data, 'fetchFlow');
    return result && result.data ? result.data : result;
};

/**
 * Создать новый поток
 * @param {string} shopId - ID магазина
 * @param {string} courseId - ID курса
 * @param {Object} flowData - Данные потока
 * @param {string} flowData.title - Название потока
 * @param {string} flowData.start_at - Дата начала курса (datetime)
 * @param {string} flowData.end_at - Дата окончания курса (datetime)
 * @param {string} [flowData.sales_start] - Дата начала продаж (datetime, по умолчанию = start_at)
 * @param {string} [flowData.sales_end] - Дата окончания продаж (datetime, по умолчанию = end_at)
 * @returns {Promise<Object>} - Созданный поток
 */
export const createFlow = async (shopId, courseId, flowData) => {
    console.log('[API] createFlow - запрос:', { shopId, courseId, flowData });
    const response = await courseAPI.post(`/courses/${shopId}/${courseId}/flows`, flowData);
    console.log('[API] createFlow - ответ сервера:', response.data);
    const result = response.data;

    // Проверка на ошибку от сервера
    ensureSuccess(result, 'createFlow');

    const finalResult = result && result.data ? result.data : result;
    console.log('[API] createFlow - финальный результат:', finalResult);

    // Дополнительная проверка что вернулся объект с id
    if (!finalResult || typeof finalResult !== 'object' || !finalResult.id) {
        console.error('[API] createFlow - некорректный формат ответа:', finalResult);
        throw new Error('Сервер вернул некорректный ответ при создании потока');
    }

    return finalResult;
};

/**
 * Обновить поток
 * @param {string} shopId - ID магазина
 * @param {string} courseId - ID курса
 * @param {string} flowId - ID потока
 * @param {Object} flowData - Обновленные данные
 * @returns {Promise<Object>} - Обновленный поток
 */
export const updateFlow = async (shopId, courseId, flowId, flowData) => {
    const response = await courseAPI.patch(`/courses/${shopId}/${courseId}/flows/${flowId}`, flowData);
    const result = ensureSuccess(response.data, 'updateFlow');
    return result && result.data ? result.data : result;
};

/**
 * Удалить поток
 * @param {string} shopId - ID магазина
 * @param {string} courseId - ID курса
 * @param {string} flowId - ID потока
 * @returns {Promise}
 */
export const deleteFlow = async (shopId, courseId, flowId) => {
    const response = await courseAPI.delete(`/courses/${shopId}/${courseId}/flows/${flowId}`);
    ensureSuccess(response.data, 'deleteFlow');
};

// ============================================
// ТАРИФЫ (Tariffs)
// ============================================

/**
 * Получить список тарифов потока
 * @param {string} shopId - ID магазина
 * @param {string} courseId - ID курса
 * @param {string} flowId - ID потока
 * @returns {Promise<Array>} - Список тарифов
 */
export const fetchTariffs = async (shopId, courseId, flowId) => {
    const response = await courseAPI.get(`/courses/${shopId}/${courseId}/flows/${flowId}/tariffs`);
    const data = response.data;

    // Обрабатываем разные форматы ответа
    if (Array.isArray(data)) {
        return data;
    }

    if (data && typeof data === 'object') {
        if (Array.isArray(data.results)) return data.results;
        if (Array.isArray(data.items)) return data.items;
        if (Array.isArray(data.tariffs)) return data.tariffs;
        if (Array.isArray(data.data)) return data.data;
    }

    console.warn('Unexpected response format from fetchTariffs:', data);
    return [];
};

/**
 * Получить один тариф
 * @param {string} shopId - ID магазина
 * @param {string} courseId - ID курса
 * @param {string} flowId - ID потока
 * @param {string} tariffId - ID тарифа
 * @returns {Promise<Object>} - Данные тарифа
 */
export const fetchTariff = async (shopId, courseId, flowId, tariffId) => {
    const response = await courseAPI.get(`/courses/${shopId}/${courseId}/flows/${flowId}/tariffs/${tariffId}`);
    const result = ensureSuccess(response.data, 'fetchTariff');
    return result && result.data ? result.data : result;
};

// ============================================
// МОДУЛИ (Modules)
// ============================================

const normalizeListResponse = (data, listKeys = []) => {
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object') {
        for (const k of listKeys) {
            if (Array.isArray(data[k])) return data[k];
        }
        if (Array.isArray(data.results)) return data.results;
        if (Array.isArray(data.items)) return data.items;
        if (Array.isArray(data.data)) return data.data;
    }
    return [];
};

/**
 * Получить список модулей курса
 */
export const fetchModules = async (shopId, courseId) => {
    const response = await courseAPI.get(`/courses/${shopId}/${courseId}/modules`);
    return normalizeListResponse(response.data, ['modules']);
};

/**
 * Создать модуль
 * fields: title, position
 */
export const createModule = async (shopId, courseId, moduleData) => {
    const response = await courseAPI.post(`/courses/${shopId}/${courseId}/modules`, moduleData);
    const result = ensureSuccess(response.data, 'createModule');
    return result && result.data ? result.data : result;
};

/**
 * Обновить модуль
 */
export const updateModule = async (shopId, courseId, moduleId, moduleData) => {
    const response = await courseAPI.patch(`/courses/${shopId}/${courseId}/modules/${moduleId}`, moduleData);
    const result = ensureSuccess(response.data, 'updateModule');
    return result && result.data ? result.data : result;
};

/**
 * Удалить модуль
 */
export const deleteModule = async (shopId, courseId, moduleId) => {
    const response = await courseAPI.delete(`/courses/${shopId}/${courseId}/modules/${moduleId}`);
    ensureSuccess(response.data, 'deleteModule');
};

// ============================================
// УРОКИ (Lessons)
// ============================================

/**
 * Получить список уроков модуля
 */
export const fetchLessons = async (shopId, courseId, moduleId) => {
    const response = await courseAPI.get(`/courses/${shopId}/${courseId}/modules/${moduleId}/lessons`);
    return normalizeListResponse(response.data, ['lessons']);
};

/**
 * Получить один урок
 */
export const fetchLesson = async (shopId, courseId, moduleId, lessonId) => {
    const response = await courseAPI.get(`/courses/${shopId}/${courseId}/modules/${moduleId}/lessons/${lessonId}`);
    const result = ensureSuccess(response.data, 'fetchLesson');
    return result && result.data ? result.data : result;
};

/**
 * Создать урок
 * fields: title, description, content, image, homework_form, published
 */
export const createLesson = async (shopId, courseId, moduleId, lessonData) => {
    const response = await courseAPI.post(`/courses/${shopId}/${courseId}/modules/${moduleId}/lessons`, lessonData);
    const result = ensureSuccess(response.data, 'createLesson');
    return result && result.data ? result.data : result;
};

/**
 * Обновить урок
 */
export const updateLesson = async (shopId, courseId, moduleId, lessonId, lessonData) => {
    const response = await courseAPI.patch(`/courses/${shopId}/${courseId}/modules/${moduleId}/lessons/${lessonId}`, lessonData);
    const result = ensureSuccess(response.data, 'updateLesson');
    return result && result.data ? result.data : result;
};

/**
 * Удалить урок
 */
export const deleteLesson = async (shopId, courseId, moduleId, lessonId) => {
    const response = await courseAPI.delete(`/courses/${shopId}/${courseId}/modules/${moduleId}/lessons/${lessonId}`);
    ensureSuccess(response.data, 'deleteLesson');
};

// ============================================
// УЧЕНИКИ (Students)
// ============================================

/**
 * Получить список учеников курса
 * @param {string} shopId - ID магазина
 * @param {string} courseId - ID курса
 * @param {string} [flowId] - ID потока (опционально, для фильтрации)
 * @param {string} [tariffId] - ID тарифа (опционально, для фильтрации)
 * @param {number} [limit] - Количество записей на странице
 * @param {number} [offset] - Смещение для пагинации
 * @returns {Promise<Object>} - Список учеников и общее количество
 */
export const fetchStudents = async (shopId, courseId, flowId = null, tariffId = null, limit = null, offset = null) => {
    const params = {};
    if (flowId) params.flow_id = flowId;
    if (tariffId) params.tariff_id = tariffId;
    if (limit !== null) params.limit = limit;
    if (offset !== null) params.offset = offset;

    const queryString = Object.keys(params).length > 0
        ? '?' + new URLSearchParams(params).toString()
        : '';

    console.log('[API] fetchStudents params:', params);

    const response = await courseAPI.get(`/courses/${shopId}/${courseId}/students`, { params });
    const data = response.data;
    console.log('[API] fetchStudents raw response:', data);

    // Сервер может вернуть { success: true, data: { students: [...], total: N } }
    // или { students_count: N, students: [...] }
    // или просто массив
    if (data && data.success !== false) {
        if (data.data && Array.isArray(data.data.students)) {
            return {
                students: data.data.students,
                total: data.data.total || data.data.students.length
            };
        }
        if (Array.isArray(data.students)) {
            return {
                students: data.students,
                total: data.students_count || data.students.length
            };
        }
        if (Array.isArray(data.data)) {
            return { students: data.data, total: data.data.length };
        }
        if (Array.isArray(data)) {
            return { students: data, total: data.length };
        }
    }

    return { students: [], total: 0 };
};

/**
 * Получить детальную информацию об ученике
 * @param {string} shopId - ID магазина
 * @param {string} courseId - ID курса
 * @param {string} studentId - ID ученика
 * @returns {Promise<Object>} - Детальная информация об ученике
 */
export const fetchStudentDetail = async (shopId, courseId, studentId) => {
    const response = await courseAPI.get(`/courses/${shopId}/${courseId}/students/${studentId}`);
    return ensureSuccess(response.data, 'fetchStudentDetail');
};

/**
 * Создать новый тариф
 * @param {string} shopId - ID магазина
 * @param {string} courseId - ID курса
 * @param {string} flowId - ID потока
 * @param {Object} tariffData - Данные тарифа
 * @param {string} tariffData.title - Название тарифа
 * @param {string} tariffData.product_id - ID товара для привязки
 * @param {Object} tariffData.access_opens_at - Когда открывается доступ
 * @param {string} tariffData.access_opens_at.access - "immediately" | "exact_date" | "days_after_payment"
 * @param {string} [tariffData.access_opens_at.date] - Дата (для exact_date)
 * @param {number} [tariffData.access_opens_at.days_count] - Кол-во дней (для days_after_payment)
 * @param {Object} tariffData.access_close_at - Когда закрывается доступ
 * @param {string} tariffData.access_close_at.access - "never" | "days_after_opening" | "exact_closing_date"
 * @param {string} [tariffData.access_close_at.date] - Дата (для exact_closing_date)
 * @param {number} [tariffData.access_close_at.days_count] - Кол-во дней (для days_after_opening)
 * @returns {Promise<Object>} - Созданный тариф
 */
export const createTariff = async (shopId, courseId, flowId, tariffData) => {
    // Используем массовое создание с одним элементом
    const result = await bulkCreateTariffs(shopId, courseId, flowId, [tariffData]);

    // Возвращаем первый (единственный) созданный тариф
    const createdTariffs = Array.isArray(result) ? result : (result.data || []);
    return createdTariffs[0] || result;
};

/**
 * Массовое создание тарифов
 * @param {string} shopId - ID магазина
 * @param {string} courseId - ID курса
 * @param {string} flowId - ID потока
 * @param {Array<Object>} tariffsData - Массив данных тарифов
 * @returns {Promise<Object>} - Созданные тарифы
 */
export const bulkCreateTariffs = async (shopId, courseId, flowId, tariffsData) => {
    const sanitizeDateToYmd = (value) => {
        if (!value) return null;
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
        const d = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(d.getTime())) return null;
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const sanitizeAccessOpensAt = (obj) => {
        const access = obj?.access || 'immediately';
        if (access === 'exact_date') {
            const date = sanitizeDateToYmd(obj?.date);
            return { access: 'exact_date', date: date ?? null, days_count: null };
        }
        if (access === 'days_after_payment') {
            const daysRaw = obj?.days_count;
            const daysNum = daysRaw === undefined || daysRaw === null || daysRaw === '' ? null : Number(daysRaw);
            return Number.isFinite(daysNum)
                ? { access: 'days_after_payment', date: null, days_count: Math.trunc(daysNum) }
                : { access: 'days_after_payment', date: null, days_count: null };
        }
        return { access: 'immediately', date: null, days_count: null };
    };

    const sanitizeAccessCloseAt = (obj) => {
        const access = obj?.access || 'never';
        if (access === 'exact_closing_date') {
            const date = sanitizeDateToYmd(obj?.date);
            return { access: 'exact_closing_date', date: date ?? null, days_count: null };
        }
        if (access === 'days_after_opening') {
            const daysRaw = obj?.days_count;
            const daysNum = daysRaw === undefined || daysRaw === null || daysRaw === '' ? null : Number(daysRaw);
            return Number.isFinite(daysNum)
                ? { access: 'days_after_opening', date: null, days_count: Math.trunc(daysNum) }
                : { access: 'days_after_opening', date: null, days_count: null };
        }
        return { access: 'never', date: null, days_count: null };
    };

    const sanitizedTariffsData = tariffsData.map(tariffData => ({
        title: tariffData?.title ?? tariffData?.name ?? '',
        product_id: tariffData?.product_id ?? tariffData?.productId ?? tariffData?.product?.id ?? tariffData?.product ?? '',
        access_opens_at: sanitizeAccessOpensAt(tariffData?.access_opens_at ?? tariffData?.accessOpensAt),
        access_close_at: sanitizeAccessCloseAt(tariffData?.access_close_at ?? tariffData?.accessCloseAt),
        accessible_modules: tariffData?.accessibleModules ?? tariffData?.accessible_modules ?? [],
        accessible_lessons: tariffData?.accessibleLessons ?? tariffData?.accessible_lessons ?? [],
    }));

    const response = await courseAPI.post(
        `/courses/${shopId}/${courseId}/flows/${flowId}/tariffs`,
        { tariffs: sanitizedTariffsData }
    );
    const result = response.data;

    // Проверяем на ошибку от сервера
    ensureSuccess(result, 'bulkCreateTariffs');

    return result && result.data ? result.data : result;
};

/**
 * Обновить тариф
 * @param {string} shopId - ID магазина
 * @param {string} courseId - ID курса
 * @param {string} flowId - ID потока
 * @param {string} tariffId - ID тарифа
 * @param {Object} tariffData - Обновленные данные
 * @returns {Promise<Object>} - Обновленный тариф
 */
export const updateTariff = async (shopId, courseId, flowId, tariffId, tariffData) => {
    const sanitizeDateToYmd = (value) => {
        if (!value) return null;
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
        const d = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(d.getTime())) return null;
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const sanitizeAccessOpensAt = (obj) => {
        const access = obj?.access || 'immediately';
        if (access === 'exact_date') {
            const date = sanitizeDateToYmd(obj?.date);
            return { access: 'exact_date', date: date ?? null, days_count: null };
        }
        if (access === 'days_after_payment') {
            const daysRaw = obj?.days_count;
            const daysNum = daysRaw === undefined || daysRaw === null || daysRaw === '' ? null : Number(daysRaw);
            return Number.isFinite(daysNum)
                ? { access: 'days_after_payment', date: null, days_count: Math.trunc(daysNum) }
                : { access: 'days_after_payment', date: null, days_count: null };
        }
        return { access: 'immediately', date: null, days_count: null };
    };

    const sanitizeAccessCloseAt = (obj) => {
        const access = obj?.access || 'never';
        if (access === 'exact_closing_date') {
            const date = sanitizeDateToYmd(obj?.date);
            return { access: 'exact_closing_date', date: date ?? null, days_count: null };
        }
        if (access === 'days_after_opening') {
            const daysRaw = obj?.days_count;
            const daysNum = daysRaw === undefined || daysRaw === null || daysRaw === '' ? null : Number(daysRaw);
            return Number.isFinite(daysNum)
                ? { access: 'days_after_opening', date: null, days_count: Math.trunc(daysNum) }
                : { access: 'days_after_opening', date: null, days_count: null };
        }
        return { access: 'never', date: null, days_count: null };
    };

    const sanitizedTariffData = {
        title: tariffData?.title ?? tariffData?.name ?? '',
        product_id: tariffData?.product_id ?? tariffData?.productId ?? tariffData?.product?.id ?? tariffData?.product ?? '',
        access_opens_at: sanitizeAccessOpensAt(tariffData?.access_opens_at ?? tariffData?.accessOpensAt),
        access_close_at: sanitizeAccessCloseAt(tariffData?.access_close_at ?? tariffData?.accessCloseAt),
        accessible_modules: tariffData?.accessibleModules ?? tariffData?.accessible_modules ?? [],
        accessible_lessons: tariffData?.accessibleLessons ?? tariffData?.accessible_lessons ?? [],
    };

    const response = await courseAPI.patch(
        `/courses/${shopId}/${courseId}/flows/${flowId}/tariffs/${tariffId}`,
        sanitizedTariffData
    );
    const result = ensureSuccess(response.data, 'updateTariff');
    return result && result.data ? result.data : result;
};

/**
 * Удалить тариф
 * @param {string} shopId - ID магазина
 * @param {string} courseId - ID курса
 * @param {string} flowId - ID потока
 * @param {string} tariffId - ID тарифа
 * @returns {Promise}
 */
export const deleteTariff = async (shopId, courseId, flowId, tariffId) => {
    const response = await courseAPI.delete(`/courses/${shopId}/${courseId}/flows/${flowId}/tariffs/${tariffId}`);
    ensureSuccess(response.data, 'deleteTariff');
};

// ============================================
// ПРОДУКТЫ (Products)
// ============================================

/**
 * Получить информацию о продукте
 * @param {string} shopId - ID магазина
 * @param {string} productId - ID продукта
 * @returns {Promise<Object>} - Данные продукта
 */
export const fetchProduct = async (shopId, productId) => {
    const response = await courseAPI.get(`/products/${shopId}/${productId}`);
    const result = ensureSuccess(response.data, 'fetchProduct');
    // API продуктов обычно возвращает { data: {...} }
    return result && result.data ? result.data : result;
};

// ============================================
// ФОРМЫ (Forms/Fillings)
// ============================================

/**
 * Получить заполнения формы учеником
 * @param {string} shopId - ID магазина
 * @param {string} formId - ID формы
 * @param {string} tgId - Telegram ID ученика
 * @returns {Promise<Array>} - Список заполнений
 */
export const fetchFormFillings = async (shopId, formId, tgId) => {
    const response = await courseAPI.get(`/forms/${shopId}/${formId}/fillings?tg_id=${tgId}`);
    const data = response.data;

    if (data && data.success && Array.isArray(data.data)) {
        return data.data;
    }

    return [];
};

export default courseAPI;
